import React, { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useBillingSubscriptions } from '../hooks/useBillingSystem';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { FacturationModeToggle } from './FacturationModeToggle';
import { useEnabledModules } from '../hooks/useSystemModules';
import { ConfigAccessModal } from './ConfigAccessModal';

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Sidebar inicia cerrado (collapsed) por defecto
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const [showConfigAccessModal, setShowConfigAccessModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { modules: enabledModules, loading: _modulesLoading } = useEnabledModules();

  // Crear un Set de paths habilitados para búsqueda O(1)
  const enabledPaths = useMemo(() => {
    const paths = new Set<string>();
    enabledModules.forEach(m => paths.add(m.path));
    return paths;
  }, [enabledModules]);

  // Helper para verificar si un módulo está habilitado por su path
  const isModuleEnabled = (path: string) => enabledPaths.has(path);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Subscribe to real-time updates
  const { invoiceCreated, invoiceDeleted, configUpdated } = useBillingSubscriptions();

  // Show notifications for real-time events
  React.useEffect(() => {
    if (invoiceCreated) {
      console.log('Nueva factura creada:', invoiceCreated);
    }
  }, [invoiceCreated]);

  React.useEffect(() => {
    if (invoiceDeleted) {
      console.log('Factura eliminada:', invoiceDeleted);
    }
  }, [invoiceDeleted]);

  React.useEffect(() => {
    if (configUpdated) {
      console.log('Configuración actualizada:', configUpdated);
    }
  }, [configUpdated]);

  const isActive = (path: string) => location.pathname === path;

  // Cerrar sidebar al navegar a otro módulo
  const handleNavClick = () => {
    setIsNavCollapsed(true);
  };

  return (
    <div className="billing-system-page">
      {/* Header */}
      <header className="page-header">
        <div className="header-content">
          <h1>MOD - Sistema de Gestión Operativa</h1>
          <p className="header-subtitle">
            Módulo de Optimización y Distribución
          </p>
        </div>
        <div className="header-user-section">
          {/* Toggle Modo Facturación */}
          <FacturationModeToggle />

          <div className="user-info">
            <span className="user-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="user-name">{user ? `${user.firstName} ${user.lastName}` : 'Usuario'}</span>
          </div>
          <button
            className="logout-button"
            onClick={handleLogout}
            title="Cerrar Sesión"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Salir</span>
          </button>
        </div>
      </header>

      {/* Navigation Overlay */}
      <div
        className={`nav-overlay ${!isNavCollapsed ? 'visible' : ''}`}
        onClick={() => setIsNavCollapsed(true)}
      />

      {/* Navigation Toggle Button */}
      <button
        className="nav-toggle-button"
        onClick={() => setIsNavCollapsed(!isNavCollapsed)}
        title={isNavCollapsed ? 'Abrir menú' : 'Cerrar menú'}
      >
        <span className="nav-toggle-icon">
          {isNavCollapsed ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          )}
        </span>
      </button>

      {/* Navigation */}
      <nav className={`page-navigation ${isNavCollapsed ? 'collapsed' : ''}`}>
        {/* Dashboard - siempre visible (es core) */}
        {isModuleEnabled('/') && (
          <Link
            to="/"
            className={`nav-button ${isActive('/') ? 'active' : ''}`}
            title="Dashboard"
            onClick={handleNavClick}
          >
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="9"/>
                <rect x="14" y="3" width="7" height="5"/>
                <rect x="14" y="12" width="7" height="9"/>
                <rect x="3" y="16" width="7" height="5"/>
              </svg>
            </span>
            <span className="nav-label">Dashboard</span>
          </Link>
        )}

        {/* Automatización Section - mostrar solo si hay módulos habilitados */}
        {(isModuleEnabled('/switch-control') || isModuleEnabled('/autorizacion')) && (
          <>
            <div className="nav-separator">
              <span className="nav-section-title">Automatización</span>
            </div>
            {isModuleEnabled('/switch-control') && (
              <Link
                to="/switch-control"
                className={`nav-button ${isActive('/switch-control') ? 'active' : ''}`}
                title="Control MOD"
                onClick={handleNavClick}
              >
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23 4 23 10 17 10"/>
                    <polyline points="1 20 1 14 7 14"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                </span>
                <span className="nav-label">Control MOD</span>
              </Link>
            )}
            {isModuleEnabled('/autorizacion') && (
              <Link
                to="/autorizacion"
                className={`nav-button ${isActive('/autorizacion') ? 'active' : ''}`}
                title="Autorización"
                onClick={handleNavClick}
              >
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </span>
                <span className="nav-label">Autorización</span>
              </Link>
            )}
          </>
        )}

        {/* Configuración de Montos Section */}
        {(isModuleEnabled('/monto-diario') || isModuleEnabled('/monto-impuestos') || isModuleEnabled('/configuracion-impuestos') || isModuleEnabled('/horarios')) && (
          <>
            <div className="nav-separator">
              <span className="nav-section-title">Configuración</span>
            </div>
            {isModuleEnabled('/monto-diario') && (
              <Link
                to="/monto-diario"
                className={`nav-button ${isActive('/monto-diario') ? 'active' : ''}`}
                title="Monto Diario"
                onClick={handleNavClick}
              >
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                </span>
                <span className="nav-label">Monto Diario</span>
              </Link>
            )}
            {isModuleEnabled('/monto-impuestos') && (
              <Link
                to="/monto-impuestos"
                className={`nav-button ${isActive('/monto-impuestos') ? 'active' : ''}`}
                title="Monto x Impuestos"
                onClick={handleNavClick}
              >
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 21h18"/>
                    <path d="M3 10h18"/>
                    <path d="M5 6l7-3 7 3"/>
                    <path d="M4 10v11"/>
                    <path d="M20 10v11"/>
                    <path d="M8 14v3"/>
                    <path d="M12 14v3"/>
                    <path d="M16 14v3"/>
                  </svg>
                </span>
                <span className="nav-label">Control Impuestos MOD</span>
              </Link>
            )}
            {isModuleEnabled('/configuracion-impuestos') && (
              <Link
                to="/configuracion-impuestos"
                className={`nav-button ${isActive('/configuracion-impuestos') ? 'active' : ''}`}
                title="Configuración de Impuestos"
                onClick={handleNavClick}
              >
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                    <path d="M9 12h6"/>
                    <path d="M9 16h6"/>
                  </svg>
                </span>
                <span className="nav-label">Config. Impuestos</span>
              </Link>
            )}
            {isModuleEnabled('/horarios') && (
              <Link
                to="/horarios"
                className={`nav-button ${isActive('/horarios') ? 'active' : ''}`}
                title="Horarios"
                onClick={handleNavClick}
              >
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </span>
                <span className="nav-label">Horarios</span>
              </Link>
            )}
          </>
        )}

        {/* Herramientas Section */}
        {isModuleEnabled('/ajustar-facturas') && (
          <>
            <div className="nav-separator">
              <span className="nav-section-title">Herramientas</span>
            </div>
            <Link
              to="/ajustar-facturas"
              className={`nav-button ${isActive('/ajustar-facturas') ? 'active' : ''}`}
              title="Ajustar Facturas Manualmente"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
              </span>
              <span className="nav-label">Ajustar Facturas</span>
            </Link>
          </>
        )}

        {/* Crear y Corregir Facturas Section */}
        {isModuleEnabled('/generar-facturas') && (
          <>
            <div className="nav-separator">
              <span className="nav-section-title">Crear y Corregir</span>
            </div>
            <Link
              to="/generar-facturas"
              className={`nav-button ${isActive('/generar-facturas') ? 'active' : ''}`}
              title="Generar Facturas Ficticias"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12" y2="12"/>
                  <line x1="9" y1="15" x2="15" y2="15"/>
                </svg>
              </span>
              <span className="nav-label">Generar Facturas</span>
            </Link>
          </>
        )}

        {/* Auditoría Section */}
        {(isModuleEnabled('/control-predictivo') || isModuleEnabled('/logs') || isModuleEnabled('/informes')) && (
          <>
            <div className="nav-separator">
              <span className="nav-section-title">Auditoría</span>
            </div>
            {isModuleEnabled('/control-predictivo') && (
              <Link
                to="/control-predictivo"
                className={`nav-button ${isActive('/control-predictivo') ? 'active' : ''}`}
                title="Control Predictivo del MOD"
                onClick={handleNavClick}
              >
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </span>
                <span className="nav-label">Control Predictivo</span>
              </Link>
            )}
            {isModuleEnabled('/logs') && (
              <Link
                to="/logs"
                className={`nav-button ${isActive('/logs') ? 'active' : ''}`}
                title="Logs de Eliminación"
                onClick={handleNavClick}
              >
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </span>
                <span className="nav-label">Logs de Eliminación</span>
              </Link>
            )}
            {isModuleEnabled('/informes') && (
              <Link
                to="/informes"
                className={`nav-button ${isActive('/informes') ? 'active' : ''}`}
                title="Informes y Reportes"
                onClick={handleNavClick}
              >
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="20" x2="18" y2="10"/>
                    <line x1="12" y1="20" x2="12" y2="4"/>
                    <line x1="6" y1="20" x2="6" y2="14"/>
                  </svg>
                </span>
                <span className="nav-label">Informes</span>
              </Link>
            )}
            <Link
              to="/seguimiento-documentos"
              className={`nav-button ${isActive('/seguimiento-documentos') ? 'active' : ''}`}
              title="Seguimiento de Documentos"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
                  <rect x="9" y="3" width="6" height="4" rx="1"/>
                  <path d="M9 14l2 2 4-4"/>
                </svg>
              </span>
              <span className="nav-label">Seguimiento Docs</span>
            </Link>
          </>
        )}

        {/* Administración Section */}
        {isModuleEnabled('/usuarios') && (
          <>
            <div className="nav-separator">
              <span className="nav-section-title">Administración</span>
            </div>
            <Link
              to="/usuarios"
              className={`nav-button ${isActive('/usuarios') ? 'active' : ''}`}
              title="Gestión de Usuarios"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </span>
              <span className="nav-label">Usuarios</span>
            </Link>
          </>
        )}

        {/* Sección Técnica - Configuración del Sistema (siempre al final) */}
        {isModuleEnabled('/configuracion') && (
          <>
            <div className="nav-spacer" />
            <div className="nav-separator nav-separator-technical">
              <span className="nav-section-title">Acceso Técnico</span>
            </div>
            <button
              onClick={() => {
                // Siempre pedir contraseña para acceder a Configuración (acceso técnico)
                setShowConfigAccessModal(true);
                handleNavClick();
              }}
              className={`nav-button nav-button-config ${isActive('/configuracion') ? 'active' : ''}`}
              title="Configuración del Sistema"
            >
              <span className="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              </span>
              <span className="nav-label">Configuración</span>
            </button>
          </>
        )}

        {/* Theme Toggle - Bubble Button */}
        <div className="theme-toggle-container">
          <button
            className={`theme-bubble-button ${theme === 'dark' ? 'dark-mode' : 'light-mode'}`}
            onClick={toggleTheme}
            title={theme === 'light' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
            aria-label={theme === 'light' ? 'Activar modo oscuro' : 'Activar modo claro'}
          >
            <div className="theme-icon-wrapper">
              <svg className="sun-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="5" fill="currentColor"/>
                <path d="M12 1v4m0 14v4M21 12h-4M7 12H3m15.364-6.364l-2.828 2.828M8.464 15.536l-2.828 2.828m12.728 0l-2.828-2.828M8.464 8.464L5.636 5.636" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <svg className="moon-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" fill="currentColor"/>
              </svg>
            </div>
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="page-content">
        {children}
      </main>

      {/* Footer */}
      <footer className="page-footer">
        <p>
          MOD - Sistema de Gestión Operativa - Todas las operaciones son registradas en
          la base espejo
        </p>
      </footer>

      {/* Modal de acceso a configuración */}
      <ConfigAccessModal
        isOpen={showConfigAccessModal}
        onClose={() => setShowConfigAccessModal(false)}
        onSuccess={() => {
          setShowConfigAccessModal(false);
          navigate('/configuracion');
        }}
      />
    </div>
  );
};
